import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Shield, ShieldAlert, CheckCircle2, X, RefreshCw, Flag, Clock, FileText, HelpCircle, Stethoscope, Download } from "lucide-react";
import { toast } from "sonner";
import VerifiedBadge from "@/components/app/VerifiedBadge";

const TYPE_LABELS = {
  text: "Publication",
  article: "Article",
  qa: "Question santé",
  clinical_case: "Cas clinique",
  alert: "Alerte santé",
  image: "Image",
};

const TYPE_ICONS = {
  article: FileText, qa: HelpCircle, clinical_case: Stethoscope, alert: ShieldAlert,
};

function DocDownloadCard({ title, description, filename, endpoint, testId }) {
  const [loading, setLoading] = useState(false);
  const download = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(endpoint, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Document téléchargé");
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message || "Téléchargement impossible");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="border border-slate-200 rounded-xl p-4 hover:border-[#618DB8] transition">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-slate-100 text-[#2A4365] flex items-center justify-center flex-shrink-0">
          <FileText className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm text-deep">{title}</div>
          <p className="text-xs text-slate-500 mt-0.5">{description}</p>
        </div>
      </div>
      <Button
        onClick={download}
        disabled={loading}
        size="sm"
        className="mt-3 w-full bg-[#2A4365] hover:bg-[#1e3050] text-white"
        data-testid={testId}
      >
        <Download className="h-3.5 w-3.5 mr-1.5" />
        {loading ? "Téléchargement…" : "Télécharger (.md)"}
      </Button>
    </div>
  );
}

function PostCard({ post, onActed }) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const act = async (action) => {
    setSubmitting(true);
    try {
      await api.post(`/admin/moderation/posts/${post.post_id}`, { action, reason });
      toast.success(action === "approve" ? "Post approuvé · publié" : action === "reject" ? "Post rejeté" : "Correction demandée");
      onActed?.();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || e.message); }
    finally { setSubmitting(false); }
  };

  const TypeIcon = TYPE_ICONS[post.type];
  const ai = post.ai_moderation || {};
  const scoreColor = ai.score >= 70 ? "bg-red-100 text-red-700 border-red-200" : ai.score >= 30 ? "bg-amber-100 text-amber-700 border-amber-200" : "bg-emerald-100 text-emerald-700 border-emerald-200";

  return (
    <article className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm" data-testid={`mod-post-${post.post_id}`}>
      {/* Header */}
      <header className="flex items-start gap-3 mb-3 flex-wrap">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#618DB8] to-[#2A4365] text-white flex items-center justify-center font-bold flex-shrink-0">
          {(post.author?.name || "?").replace(/^Dr\s+/, "").charAt(0)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm flex items-center gap-1 flex-wrap">
            {post.author?.name}{post.author?.blue_check && <VerifiedBadge size={11} />}
            <span className="ms-2 inline-flex items-center gap-1 bg-slate-100 text-slate-600 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase">
              {TypeIcon && <TypeIcon className="h-2.5 w-2.5" />}{TYPE_LABELS[post.type] || post.type}
            </span>
          </div>
          <div className="text-xs text-slate-500">{post.author?.specialty || post.author?.role} · {new Date(post.created_at).toLocaleString("fr-FR")}</div>
        </div>
        <div className={`text-[10px] font-bold uppercase rounded-full px-2 py-1 border flex items-center gap-1 ${scoreColor}`} data-testid={`ai-score-${post.post_id}`}>
          <Shield className="h-3 w-3" />IA: {ai.score ?? "-"} · {ai.verdict || "—"}
        </div>
      </header>

      {/* Content */}
      {post.title && <div className="font-bold text-deep mb-1">{post.title}</div>}
      <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed mb-3">{post.content}</p>
      {post.image_url && <img src={post.image_url} alt="" className="rounded-lg max-h-64 w-full object-cover mb-3 border border-slate-100" />}

      {/* AI flags */}
      {ai.flags?.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-1.5">
          {ai.flags.map((f) => (
            <span key={f} className="text-[10px] bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-semibold">⚑ {f}</span>
          ))}
        </div>
      )}

      {/* Anonymization checklist for clinical case */}
      {post.type === "clinical_case" && post.anonymization_checklist && (
        <div className="mb-3 bg-slate-50 border border-slate-200 rounded-lg p-3">
          <div className="text-xs font-bold text-slate-700 mb-1">Checklist anonymisation</div>
          <div className="text-xs text-slate-600">
            {post.anonymization_checklist.every(Boolean) ? "✓ Tous les 8 points cochés par l'auteur" : "⚠ Checklist incomplète"}
          </div>
        </div>
      )}

      {/* Reports */}
      {post.reports?.length > 0 && (
        <div className="mb-3 bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="text-xs font-bold text-red-700 mb-1 flex items-center gap-1">
            <Flag className="h-3 w-3" />{post.reports.length} signalement{post.reports.length > 1 ? "s" : ""}
          </div>
          <ul className="text-xs text-red-700 space-y-0.5">
            {post.reports.map((r) => (
              <li key={r.report_id}>· <strong>{r.category}</strong>{r.comment ? ` — ${r.comment}` : ""}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Actions */}
      <div className="border-t border-slate-100 pt-3 mt-3 space-y-2">
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Motif de la décision (optionnel)…" className="text-sm" data-testid={`mod-reason-${post.post_id}`} />
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => act("approve")} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`mod-approve-${post.post_id}`}>
            <CheckCircle2 className="h-4 w-4 me-1" />Approuver
          </Button>
          <Button onClick={() => act("request_correction")} disabled={submitting} variant="outline" className="border-amber-400 text-amber-700" data-testid={`mod-correction-${post.post_id}`}>
            <RefreshCw className="h-4 w-4 me-1" />Demander correction
          </Button>
          <Button onClick={() => act("reject")} disabled={submitting} variant="destructive" data-testid={`mod-reject-${post.post_id}`}>
            <X className="h-4 w-4 me-1" />Rejeter
          </Button>
        </div>
      </div>
    </article>
  );
}

export default function AdminModeration() {
  const { user } = useAuth();
  const [queue, setQueue] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: q }, { data: s }] = await Promise.all([
        api.get("/admin/moderation/queue"),
        api.get("/admin/moderation/stats"),
      ]);
      setQueue(q); setStats(s);
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (user?.role !== "admin" && !user?.is_admin) {
    return (
      <div className="p-10 max-w-2xl mx-auto text-center">
        <ShieldAlert className="h-12 w-12 mx-auto text-red-500 mb-3" />
        <h1 className="font-display text-2xl font-bold text-deep">Accès réservé</h1>
        <p className="text-slate-500 mt-2">Cette interface est réservée aux administrateurs Doctobook.</p>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-10 max-w-5xl mx-auto">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-red-700 mb-2">Back-office · Modération</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-deep tracking-tight">File de modération</h1>
        <p className="text-sm text-slate-500 mt-1">Validation systématique sous SLA 4 h · CDC v1.0 US-74</p>
      </div>

      {/* Documents internes admin */}
      <section className="mb-8 bg-white border border-slate-200 rounded-2xl p-5" data-testid="admin-docs-section">
        <div className="flex items-center gap-2 mb-3">
          <FileText className="h-4 w-4 text-[#2A4365]" />
          <h2 className="font-display text-lg font-bold text-deep">Documents internes</h2>
          <span className="ms-auto text-[10px] uppercase font-semibold bg-red-50 text-red-700 border border-red-200 rounded-full px-2 py-0.5">Confidentiel</span>
        </div>
        <p className="text-xs text-slate-500 mb-4">Documents stratégiques et techniques réservés aux administrateurs Doctobook.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <DocDownloadCard
            title="Playbook Migration OVH HDS"
            description="Guide pas-à-pas Public Cloud → HDS certifié (11 étapes, ~1 journée)."
            filename="Doctobook_Playbook_Migration_OVH.md"
            endpoint="/docs/ovh-migration-playbook"
            testId="download-ovh-playbook"
          />
          <DocDownloadCard
            title="Brief exécutif · Migration OVH HDS"
            description="Résumé décisionnel : identité, périmètre HDS, budget, planning, contacts OVHcloud."
            filename="Doctobook_Brief_Migration_OVH_HDS.md"
            endpoint="/docs/admin/ovh-hds-brief"
            testId="download-ovh-brief"
          />
          <DocDownloadCard
            title="Email · Demande d'activation HDS OVH"
            description="Modèle prêt à envoyer à sales-fr@ovhcloud.com (cc healthcare@) pour activer l'offre HDS."
            filename="Doctobook_Email_Activation_HDS_OVH.md"
            endpoint="/docs/admin/ovh-hds-activation-email"
            testId="download-ovh-email"
          />
          <DocDownloadCard
            title="Stripe Connect · Activation"
            description="Étapes pour sortir du mode démo : réclamer le sandbox, activer Connect Express, webhook."
            filename="Doctobook_Stripe_Connect_Activation.md"
            endpoint="/docs/admin/stripe-connect-activation"
            testId="download-stripe-connect-doc"
          />
          <DocDownloadCard
            title="GoCardless · Mise en service"
            description="Créer le compte sandbox/live, jeton d'accès, webhook, test du mandat SEPA."
            filename="Doctobook_GoCardless_Mise_en_service.md"
            endpoint="/docs/admin/gocardless-onboarding"
            testId="download-gocardless-doc"
          />
          <DocDownloadCard
            title="Vision V2 · Marketplaces"
            description="Roadmap stratégique Doctobook Trans & Doctobook Rx (transport / pharmacies)."
            filename="Doctobook_Vision_Marketplaces.md"
            endpoint="/docs/vision-marketplaces"
            testId="download-vision-doc"
          />
        </div>
      </section>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          {[
            { label: "Posts en attente", value: stats.pending_posts, icon: Clock, color: "amber" },
            { label: "Signalements ouverts", value: stats.pending_reports, icon: Flag, color: "red" },
            { label: "Posts aujourd'hui", value: stats.posts_today, icon: FileText, color: "blue" },
            { label: "Reports aujourd'hui", value: stats.reports_today, icon: Flag, color: "slate" },
          ].map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.label} className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs text-slate-500 font-semibold uppercase">{s.label}</div>
                    <div className="text-2xl font-extrabold text-deep mt-1" data-testid={`stat-${s.label.replace(/ /g, "-")}`}>{s.value}</div>
                  </div>
                  <Icon className={`h-5 w-5 text-${s.color}-500`} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-slate-400">Chargement de la file…</div>
      ) : queue.length === 0 ? (
        <div className="text-center py-12 text-slate-500 bg-emerald-50 border border-emerald-200 rounded-xl">
          <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-500 mb-2" />
          File vide ! Aucun contenu en attente.
        </div>
      ) : (
        <div className="space-y-4">
          {queue.map((p) => <PostCard key={p.post_id} post={p} onActed={load} />)}
        </div>
      )}
    </div>
  );
}
